import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { GoogleMap, TrafficLayer, Polyline, Marker, OverlayView, useJsApiLoader } from "@react-google-maps/api";
import { Box, Button, Typography } from "@mui/material";
import mapStyles from "../../assets/estilos/mapStyles";
import ClusterManager from "./ClusterManager";

const MostrarMapa = ({ 
  vehiculos, 
  centro, 
  seguimientoActivo, 
  onDesactivarSeguimiento,
  viajeEnCurso 
}) => {
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const ultimaActualizacionRef = useRef({});
  const seguimientoMarkerRef = useRef(null);
  const markerStatesRef = useRef({});
  
  const [zoomActual, setZoomActual] = useState(11);
  const [mapReady, setMapReady] = useState(false);
  const [marcadoresListos, setMarcadoresListos] = useState(0);
  const [mapTypeId, setMapTypeId] = useState("roadmap");
  const [posicionAnimadaVehiculo, setPosicionAnimadaVehiculo] = useState(null);
  const [hoveredInfo, setHoveredInfo] = useState(null);

  const initialFitDone = useRef(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
  });

  // 🔴 CREADOR DEL ICONO MODERNO DE PARADA / CLUSTER (Suma minutos con indicador visual)
  const createStopClusterIcon = useCallback((totalMinutes) => {
    const google = window.google;
    if (!google) return null;

    const size = totalMinutes < 60 ? 40 : totalMinutes < 180 ? 48 : 56;
    const displayTime = totalMinutes > 999 ? "+99" : totalMinutes;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <defs>
          <linearGradient id="stopGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#ef4444" />
            <stop offset="100%" stop-color="#991b1b" />
          </linearGradient>
          <filter id="stopShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="4" stdDeviation="4" flood-color="rgba(0,0,0,0.5)" />
          </filter>
        </defs>
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="url(#stopGrad)" opacity="0.25" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 8}" fill="url(#stopGrad)" filter="url(#stopShadow)" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 9}" fill="none" stroke="rgba(255,255,255,0.9)" stroke-width="2" />
        <text x="50%" y="45%" dominant-baseline="middle" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="${size * 0.26}" font-weight="900" fill="#ffffff" letter-spacing="-0.5">${displayTime}</text>
        <text x="50%" y="67%" dominant-baseline="middle" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="${size * 0.16}" font-weight="bold" fill="#fca5a5" text-transform="uppercase">min</text>
      </svg>
    `;

    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size / 2, size / 2)
    };
  }, []);

  // 📍 CREADOR DEL PIN PIN DE INICIO
  const getStartMarkerIcon = useCallback(() => {
    const maps = window.google?.maps;
    if (!maps) return undefined;

    const svg = `
      <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="pinShadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="rgba(0,0,0,0.25)" />
          </filter>
        </defs>
        <path d="M16 2C8.268 2 2 8.268 2 16c0 8.428 11.723 19.458 13.28 20.89a2 2 0 0 0 2.71 0C19.277 35.458 30 24.428 30 16 30 8.268 23.732 2 16 2z" 
              fill="#2FA4B8" filter="url(#pinShadow)"/>
        <circle cx="16" cy="16" r="10" fill="#ffffff" />
        <text x="16" y="20.5" font-size="12" font-family="Inter, Arial, sans-serif" font-weight="900" text-anchor="middle" fill="#2FA4B8">A</text>
      </svg>
    `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new maps.Size(32, 40),
      anchor: new maps.Point(16, 40)
    };
  }, []);

  // 🛑 CLUSTERING DE PARADAS DINÁMICO SEGÚN ZOOM
  const stopClusters = useMemo(() => {
    if (!isLoaded || !viajeEnCurso?.in_trip || !viajeEnCurso.trip?.stops) return [];

    const allStops = viajeEnCurso.trip.stops;

    if (zoomActual >= 16) {
      return allStops.map((s) => ({ isCluster: false, ...s }));
    }

    const threshold =
      zoomActual <= 10 ? 0.08 :
      zoomActual <= 12 ? 0.03 :
      zoomActual <= 14 ? 0.01 : 0.004;

    const result = [];
    const usados = new Set();

    for (let i = 0; i < allStops.length; i++) {
      if (usados.has(i)) continue;
      const s1 = allStops[i];
      const lat1 = Number(s1.lat);
      const lng1 = Number(s1.lng);
      if (isNaN(lat1) || isNaN(lng1)) continue;

      const clusterGroup = [s1];
      usados.add(i);

      for (let j = i + 1; j < allStops.length; j++) {
        if (usados.has(j)) continue;
        const s2 = allStops[j];
        const lat2 = Number(s2.lat);
        const lng2 = Number(s2.lng);
        if (isNaN(lat2) || isNaN(lng2)) continue;

        if (Math.abs(lat1 - lat2) < threshold && Math.abs(lng1 - lng2) < threshold) {
          clusterGroup.push(s2);
          usados.add(j);
        }
      }

      if (clusterGroup.length > 1) {
        const avgLat = clusterGroup.reduce((sum, s) => sum + Number(s.lat), 0) / clusterGroup.length;
        const avgLng = clusterGroup.reduce((sum, s) => sum + Number(s.lng), 0) / clusterGroup.length;
        const totalMinutes = clusterGroup.reduce((sum, s) => sum + (Number(s.minutes) || 0), 0);

        result.push({
          isCluster: true,
          lat: avgLat,
          lng: avgLng,
          totalStops: clusterGroup.length,
          totalMinutes: totalMinutes,
          stops: clusterGroup
        });
      } else {
        result.push({
          isCluster: false,
          ...s1
        });
      }
    }
    return result;
  }, [viajeEnCurso, zoomActual, isLoaded]);

  // 📌 Encuadrar la cámara cuando carga el viaje
  useEffect(() => {
    if (!mapRef.current || !mapReady) return;

    if (viajeEnCurso?.in_trip && viajeEnCurso?.trip?.points?.length > 0) {
      const google = window.google;
      const bounds = new google.maps.LatLngBounds();

      viajeEnCurso.trip.points.forEach((p) => {
        bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) });
      });

      mapRef.current.fitBounds(bounds);
    }
  }, [viajeEnCurso, mapReady]);

  // 📌 Centrado general
  useEffect(() => {
    if (!mapRef.current || !mapReady || !vehiculos || vehiculos.length === 0) return;

    if (seguimientoActivo && centro) {
      mapRef.current.panTo(centro);
      mapRef.current.setZoom(17);
      return;
    }

    if (!initialFitDone.current && (!viajeEnCurso || !viajeEnCurso.in_trip)) {
      const google = window.google;
      const bounds = new google.maps.LatLngBounds();
      let hasValidCoords = false;

      vehiculos.forEach((v) => {
        const lat = Number(v.latitude);
        const lng = Number(v.longitude);
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
          bounds.extend({ lat, lng });
          hasValidCoords = true;
        }
      });

      if (hasValidCoords) {
        mapRef.current.fitBounds(bounds);
        initialFitDone.current = true;
      }
    }
  }, [vehiculos, centro, seguimientoActivo, mapReady, viajeEnCurso]);

  // Animación del ícono de vehículo
  const animateMarker = (marker, markerId, newPosition) => {
    const now = Date.now();

    if (!markerStatesRef.current[markerId]) {
      const currentPos = marker.getPosition();
      markerStatesRef.current[markerId] = {
        animationFrame: null,
        currentPosition: currentPos ? { lat: currentPos.lat(), lng: currentPos.lng() } : newPosition,
        targetPosition: newPosition,
        startPosition: newPosition,
        startTime: now,
        duration: 10000,
        lastUpdateTime: now - 10000
      };
    }

    const state = markerStatesRef.current[markerId];

    if (state.animationFrame) {
      cancelAnimationFrame(state.animationFrame);
    }

    let calculatedDuration = now - state.lastUpdateTime;
    if (calculatedDuration < 2000 || calculatedDuration > 25000) {
      calculatedDuration = 10000; 
    }

    state.lastUpdateTime = now;
    state.startPosition = { lat: state.currentPosition.lat, lng: state.currentPosition.lng };
    state.targetPosition = { lat: Number(newPosition.lat), lng: Number(newPosition.lng) };
    state.startTime = now;
    state.duration = calculatedDuration; 

    const animate = () => {
      const elapsed = Date.now() - state.startTime;
      let progress = elapsed / state.duration;

      if (progress > 1) progress = 1;

      const lat = state.startPosition.lat + (state.targetPosition.lat - state.startPosition.lat) * progress;
      const lng = state.startPosition.lng + (state.targetPosition.lng - state.startPosition.lng) * progress;

      const nextPosition = { lat, lng };
      state.currentPosition = nextPosition;
      marker.setPosition(nextPosition);

      if (seguimientoActivo && seguimientoMarkerRef.current === marker) {
        setPosicionAnimadaVehiculo(nextPosition);
        if (mapRef.current) {
          mapRef.current.moveCamera({ center: nextPosition });
        }
      }

      if (progress < 1) {
        state.animationFrame = requestAnimationFrame(animate);
      }
    };

    animate();
  };

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !mapReady) return;

    const google = window.google;
    const nuevosIds = {};

    vehiculos.forEach((v) => {
      if (!v.latitude || !v.longitude) return;

      const id = v.idVehiculo;
      nuevosIds[id] = true;

      if (markersRef.current[id]) {
        animateMarker(markersRef.current[id], id, {
          lat: Number(v.latitude),
          lng: Number(v.longitude),
        });
      } else {
        const marker = new google.maps.Marker({
          position: {
            lat: Number(v.latitude),
            lng: Number(v.longitude),
          },
          title: `${v.modelo} - ${v.patente}`,
          icon: {
            url: v.imagen,
            scaledSize: new google.maps.Size(45, 45),
          },
        });

        markersRef.current[id] = marker;
      }

      if (
        seguimientoActivo &&
        centro &&
        Math.abs(v.latitude - centro.lat) < 0.0001 &&
        Math.abs(v.longitude - centro.lng) < 0.0001
      ) {
        seguimientoMarkerRef.current = markersRef.current[id];
      }
    });

    Object.keys(markersRef.current).forEach((id) => {
      if (!nuevosIds[id]) {
        markersRef.current[id].setMap(null);
        delete markersRef.current[id];
        delete ultimaActualizacionRef.current[id];

        if (markerStatesRef.current[id]) {
          if (markerStatesRef.current[id].animationFrame) {
            cancelAnimationFrame(markerStatesRef.current[id].animationFrame);
          }
          delete markerStatesRef.current[id];
        }
      }
    });

    setMarcadoresListos(Date.now());
  }, [vehiculos, isLoaded, mapReady, seguimientoActivo, centro]);

  if (!isLoaded) return null;

  // Ruta animada sincronizada
  const calcularRutaAnimada = () => {
    if (!viajeEnCurso?.in_trip || !viajeEnCurso.trip?.points) return [];

    const puntosOriginales = viajeEnCurso.trip.points.map((p) => ({
      lat: Number(p.lat),
      lng: Number(p.lng)
    }));

    if (!seguimientoActivo || !posicionAnimadaVehiculo) {
      return puntosOriginales;
    }

    const puntosAnimados = [...puntosOriginales];
    if (puntosAnimados.length > 0) {
      puntosAnimados[puntosAnimados.length - 1] = posicionAnimadaVehiculo;
    }

    return puntosAnimados;
  };

  const rutaCoordenadas = calcularRutaAnimada();

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        zoom={11}
        center={centro || { lat: -33.4489, lng: -70.6693 }}
        mapTypeId={mapTypeId}
        options={{
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControl: false,
          rotateControl: false,
          styles: mapStyles,
          draggable: true,
          gestureHandling: "greedy",
        }}
        onZoomChanged={() => {
          if (!mapRef.current) return;
          const zoom = Math.round(mapRef.current.getZoom());
          setZoomActual((prev) => (prev === zoom ? prev : zoom));
        }}
        onLoad={(map) => {
          mapRef.current = map;
          setMapReady(true);
        }}
        onDragStart={() => {
          if (seguimientoActivo && onDesactivarSeguimiento) {
            onDesactivarSeguimiento();
            seguimientoMarkerRef.current = null;
            setPosicionAnimadaVehiculo(null);
          }
        }}
      >
        <TrafficLayer />

        {/* 🛣️ RUTA DEL VIAJE Y ELEMENTOS VISUALES */}
        {viajeEnCurso?.in_trip && (
          <>
            {/* TRAZADO PRINCIPAL */}
            <Polyline
              path={rutaCoordenadas}
              options={{
                strokeColor: "#2FA4B8",
                strokeOpacity: 0.9,
                strokeWeight: 5,
              }}
            />

            {/* 🔵 MARCADOR DE ORIGEN ESTILO MODAL (Pin "A") */}
            {viajeEnCurso.trip.start_point && (
              <Marker
                position={{
                  lat: Number(viajeEnCurso.trip.start_point.lat),
                  lng: Number(viajeEnCurso.trip.start_point.lng),
                }}
                icon={getStartMarkerIcon()}
                zIndex={900}
                onMouseOver={() => {
                  setHoveredInfo({
                    position: {
                      lat: Number(viajeEnCurso.trip.start_point.lat),
                      lng: Number(viajeEnCurso.trip.start_point.lng)
                    },
                    type: "start",
                    timestamp: viajeEnCurso.trip.start_time
                  });
                }}
                onMouseOut={() => setHoveredInfo(null)}
              />
            )}

            {/* 🛑 PARADAS CLUSTERIZADAS DINÁMICAS (Exactas a ModalHistorial) */}
            {stopClusters.map((cluster, idx) => {
              const lat = Number(cluster.lat);
              const lng = Number(cluster.lng);
              const isCluster = cluster.isCluster;
              const minutes = isCluster ? cluster.totalMinutes : (cluster.minutes || 0);
              const stopsCount = isCluster ? cluster.totalStops : 1;
              const timestamp = cluster.start_time;

              return (
                <Marker
                  key={`stop-marker-${idx}`}
                  position={{ lat, lng }}
                  icon={createStopClusterIcon(minutes)}
                  zIndex={998}
                  onClick={() => {
                    if (mapRef.current) {
                      mapRef.current.panTo({ lat, lng });
                      mapRef.current.setZoom(Math.min(zoomActual + 2, 18));
                    }
                  }}
                  onMouseOver={() => {
                    setHoveredInfo({
                      position: { lat, lng },
                      stop: true,
                      isCluster: isCluster,
                      totalStops: stopsCount,
                      minutes: minutes,
                      timestamp: timestamp
                    });
                  }}
                  onMouseOut={() => setHoveredInfo(null)}
                />
              );
            })}

            {/* 💬 TOOLTIP MODERNO (Hover sobre paradas o inicio) */}
            {hoveredInfo && (
              <OverlayView
                position={hoveredInfo.position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: "10px",
                    px: 2,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    minWidth: 170,
                    borderLeft: hoveredInfo.stop ? "4px solid #ef4444" : "4px solid #2FA4B8"
                  }}
                >
                  {hoveredInfo.stop ? (
                    <>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#b91c1c" }}>
                        {hoveredInfo.isCluster ? `🛑 Agrupación (${hoveredInfo.totalStops})` : `🛑 Parada Fija`}
                      </Typography>
                      <Typography sx={{ fontSize: 13, mt: 0.5, color: "#334155" }}>
                        <b>⏱ {hoveredInfo.minutes} min</b> detenido{hoveredInfo.isCluster && "s en la zona"}
                      </Typography>
                      {!hoveredInfo.isCluster && hoveredInfo.timestamp && (
                        <Typography sx={{ fontSize: 11, mt: 1, color: "#64748b", fontWeight: 500 }}>
                          🕒 Inicio: {new Date(hoveredInfo.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2FA4B8" }}>
                        🚀 Punto de Origen
                      </Typography>
                      {hoveredInfo.timestamp && (
                        <Typography sx={{ fontSize: 11, mt: 0.5, color: "#64748b" }}>
                          🕒 {new Date(hoveredInfo.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </OverlayView>
            )}
          </>
        )}

        {mapReady && (
          <ClusterManager
            map={mapRef.current}
            vehiculos={vehiculos}
            zoomActual={zoomActual}
            markersRef={markersRef}
            seguimientoActivo={seguimientoActivo}
            seguimientoMarkerRef={seguimientoMarkerRef}
            marcadoresListos={marcadoresListos}
          />
        )}
      </GoogleMap>

      {/* BOTÓN VISTA MAPA / SATÉLITE */}
      <Box sx={{ position: "absolute", top: 20, left: 20, zIndex: 10 }}>
        <Button
          onClick={() => setMapTypeId(prev => (prev === "roadmap" ? "satellite" : "roadmap"))}
          sx={{
            background: "#ffffff",
            color: "#1e293b",
            height: 44,
            px: 2.2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            borderRadius: "24px",
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "none",
            border: "1px solid rgba(0,0,0,0.06)",
            "&:hover": {
              background: "#f8fafc",
              color: "#2FA4B8"
            }
          }}
        >
          {mapTypeId === "satellite" ? "Vista Mapa" : "Vista Satélite"}
        </Button>
      </Box>
    </Box>
  );
};

export class DefaultMap {}
export default MostrarMapa;